import { useEffect, useRef, useState } from "react";
import { adminApi, type ViewerSearchHit } from "@/lib/admin";

type Props = {
  value: string;                                 // current viewerId (GUID) or empty
  onChange: (viewerId: string, hit?: ViewerSearchHit) => void;
  placeholder?: string;
};

/**
 * Brutalist-style typeahead picker for viewers. Replaces the raw GUID input
 * that admins would otherwise have to source from somewhere. Debounced
 * search (250ms) so we don't hammer /api/admin/viewers/search on every
 * keystroke; results dropdown closes on outside-click or selection.
 *
 * Stores the GUID in the parent (that's what the graph preset endpoint
 * needs), but displays the friendly kickUsername in the input field once
 * a hit has been selected.
 */
export function ViewerPicker({ value, onChange, placeholder = "Wpisz nick widza…" }: Props) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<ViewerSearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ViewerSearchHit | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click — important for combobox UX so the user doesn't
  // get a stale dropdown hovering when they tab away.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Reset display label if parent cleared the value (e.g. preset switch).
  useEffect(() => {
    if (!value) {
      setSelected(null);
      setQuery("");
    }
  }, [value]);

  // Debounced search — fire 250ms after typing stops. We let an empty query
  // through to seed the dropdown with top-by-rank viewers, makes the picker
  // useful even without typing.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await adminApi.viewerSearch(query, 20);
        setHits(r);
      } catch (e) {
        setError((e as Error).message);
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  function pick(hit: ViewerSearchHit) {
    setSelected(hit);
    setQuery(hit.kickUsername);
    setOpen(false);
    onChange(hit.viewerId, hit);
  }

  function clearPick() {
    setSelected(null);
    setQuery("");
    onChange("", undefined);
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex gap-2">
        <input
          type="text"
          className="input-brut flex-1"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(null);
            onChange("", undefined);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
        />
        {selected && (
          <button type="button" className="btn-ghost px-3 py-1" onClick={clearPick} title="Wyczyść">
            ✕
          </button>
        )}
      </div>

      {selected && (
        <div className="mt-1 text-xs text-carp-ink/60 font-mono">
          {selected.viewerId}
        </div>
      )}

      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 brut bg-white max-h-80 overflow-auto">
          {loading && <div className="px-3 py-2 text-sm text-carp-ink/60">Szukam…</div>}
          {error && <div className="px-3 py-2 text-sm text-carp-destructive">{error}</div>}
          {!loading && !error && hits.length === 0 && (
            <div className="px-3 py-2 text-sm text-carp-ink/60">Brak wyników.</div>
          )}
          <ul>
            {hits.map((h) => (
              <li
                key={h.viewerId}
                className="px-3 py-2 cursor-pointer hover:bg-carp-yellow/30 border-b border-carp-ink/10 text-sm flex items-center justify-between gap-3"
                onClick={() => pick(h)}
              >
                <div className="flex flex-col">
                  <span className="font-bold">{h.kickUsername}</span>
                  <span className="text-xs text-carp-ink/60 font-mono">
                    {h.chatMessageCount} msg · {h.totalDonatedPln.toFixed(2)} zł
                    {h.isSubscriber && " · SUB"}
                  </span>
                </div>
                <span className="text-xs text-carp-ink/40">rank {h.rankScore.toFixed(0)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
